import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { Check } from '@mui/icons-material';
import T from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Modal } from 'features/common/MaterialBasedComponents';
import { Spinner, TextForm } from 'features/common';
import {
  selectMatchedUrl,
  selectFolders,
  selectCurrentCaseId,
} from 'common/selectors';
import { generateFolderTimestamp } from '../utils';
import MoveDocument from './MoveDocument';
import { updateReferences } from '../../redux/updateReferences';
import { assignDocuments } from '../../redux/assignDocuments';
import { addFolder } from '../../redux/addFolder';
import { fetchContainer } from '../../redux/fetchContainer';
import { generateReferenceCandidates } from '../../redux/generateReferenceCandidates';
import { generateReferenceSequence } from '../../redux/generateReferenceSequence';
import {
  selectGenerateReferenceCandidatesPending,
  selectGenerateReferenceCandidatesResult,
  selectGenerateReferenceSequencePending,
  selectGenerateReferenceSequenceResult,
  selectUpdateReferencesPending,
  selectUpdateReferencesResult,
} from '../../redux/selectors';
import {
  dismissGenerateReferenceCandidatesFeedback,
  dismissGenerateReferenceSequenceFeedback,
} from '../../redux/actions';
import './ModalMerge.scss';

const ModalUpdateReferences = ({ show, handleClose, selectedRows }) => {
  const dispatch = useDispatch();
  const matchedUrl = useSelector(selectMatchedUrl);
  const file = selectedRows?.[selectedRows.length - 1] || null;
  const folders = useSelector(selectFolders);
  const updateReferencesPending = useSelector(selectUpdateReferencesPending);
  const candidatesPending = useSelector(selectGenerateReferenceCandidatesPending);
  const candidatesResult = useSelector(selectGenerateReferenceCandidatesResult);
  const sequencePending = useSelector(selectGenerateReferenceSequencePending);
  const sequenceResult = useSelector(selectGenerateReferenceSequenceResult);
  const updateReferencesResult = useSelector(selectUpdateReferencesResult);
  const updateReferencesCompletedByFileId = useSelector(
    (state) => state.case.updateReferencesCompletedByFileId || {},
  );

  const [activeStep, setActiveStep] = useState(0);
  const [sequenceError, setSequenceError] = useState('');
  const [sequenceInProgress, setSequenceInProgress] = useState(false);

  const hasSelection = Boolean(selectedRows && selectedRows.length);
  const fileId = file?.id;
  const fileName = file?.name || file?.originalName || '';
  const fileType = (file?.type || '').toLowerCase();
  const fileExtension = (fileName.split('.').pop() || '').toLowerCase();
  const isDocxFile = fileType === 'docx';

  const isPresentModePage =
    Boolean(matchedUrl && matchedUrl.includes('present-'));
  const isRecycleBin = Boolean(matchedUrl && matchedUrl.includes('recycle-bin'));

  const withFileId = useCallback((text) => (fileId ? `${text} - ${fileId}` : text), [fileId]);

  const defaultBundleName = useMemo(() => {
    const trimmedFileName = fileName.replace(/\.[^/.]+$/, '');
    return T.translate('case.updateReferencesSequenceDefaultName', {
      fileName: trimmedFileName || T.translate('case.allDocuments'),
    });
  }, [fileName]);
  const defaultSequenceBundleName = useMemo(
    () => `${defaultBundleName} - ${generateFolderTimestamp()}`,
    [defaultBundleName],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      folder: 'auto-generated',
      bundleName: defaultSequenceBundleName,
    },
  });

  const watchedBundleName = watch('bundleName');

  useEffect(() => {
    if (!show) return;
    setActiveStep(0);
    setSequenceError('');
    reset({ folder: 'auto-generated', bundleName: defaultSequenceBundleName });
    dispatch(dismissGenerateReferenceCandidatesFeedback());
    dispatch(dismissGenerateReferenceSequenceFeedback());
  }, [show, fileId, defaultSequenceBundleName, reset, dispatch]);

  const updatedReferences = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return [];
    }
    if (Array.isArray(candidatesResult.updatedReferences)) {
      return candidatesResult.updatedReferences;
    }
    return Array.isArray(candidatesResult.candidates) ? candidatesResult.candidates : [];
  }, [candidatesResult, fileId]);

  const fileDocumentUpdates = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return [];
    }
    return Array.isArray(candidatesResult.fileDocumentUpdates)
      ? candidatesResult.fileDocumentUpdates
      : [];
  }, [candidatesResult, fileId]);

  const collisionReassignments = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return [];
    }
    return Array.isArray(candidatesResult.collisionReassignments)
      ? candidatesResult.collisionReassignments
      : [];
  }, [candidatesResult, fileId]);

  const unresolvedReferences = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return [];
    }
    return Array.isArray(candidatesResult.unresolvedReferences)
      ? candidatesResult.unresolvedReferences
      : [];
  }, [candidatesResult, fileId]);

  const referencesFoundCount = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return 0;
    }
    const count = Number(candidatesResult.referencesFound);
    if (Number.isFinite(count)) {
      return count;
    }
    return updatedReferences.length;
  }, [candidatesResult, fileId, updatedReferences.length]);

  const referencesUpdatedCount = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return 0;
    }
    const count = Number(candidatesResult.referencesUpdated);
    if (Number.isFinite(count)) {
      return count;
    }
    return updatedReferences.length;
  }, [candidatesResult, fileId, updatedReferences.length]);

  const candidateReferences = useMemo(() => {
    if (!candidatesResult || candidatesResult.fileId !== fileId) {
      return [];
    }
    return Array.isArray(candidatesResult.allReferences) ? candidatesResult.allReferences : [];
  }, [candidatesResult, fileId]);

  const updateReferencesAllReferences = useMemo(() => {
    if (!updateReferencesResult || updateReferencesResult.fileId !== fileId) {
      return [];
    }
    return Array.isArray(updateReferencesResult.allReferences)
      ? updateReferencesResult.allReferences
      : [];
  }, [updateReferencesResult, fileId]);

  const referencesForSequence = updateReferencesAllReferences.length
    ? updateReferencesAllReferences
    : candidateReferences;

  const candidatesGenerated = Boolean(candidatesResult && candidatesResult.fileId === fileId);
  const referencesUpdated = Boolean(fileId && updateReferencesCompletedByFileId[fileId]);
  const sequenceGenerated = Boolean(sequenceResult && sequenceResult.fileId === fileId);
  const hasUpdateCandidates =
    updatedReferences.length > 0 ||
    fileDocumentUpdates.length > 0 ||
    collisionReassignments.length > 0;
  const noCandidates = candidatesGenerated && !hasUpdateCandidates;
  const hasUnresolvedReferences = unresolvedReferences.length > 0;
  const hasPreviewData =
    updatedReferences.length > 0 ||
    fileDocumentUpdates.length > 0 ||
    collisionReassignments.length > 0 ||
    hasUnresolvedReferences;
  const unresolvedReferenceRows = useMemo(
    () =>
      unresolvedReferences.map((item, index) => {
        if (item === null || item === undefined) {
          return { key: `unresolved-${index}`, reference: '-' };
        }
        if (typeof item === 'string' || typeof item === 'number') {
          return { key: `unresolved-${index}`, reference: String(item) };
        }
        if (typeof item !== 'object') {
          return { key: `unresolved-${index}`, reference: String(item) };
        }
        const referenceValue =
          item.reference ||
          item.oldReference ||
          item.newReference ||
          item.value ||
          item.text ||
          item.display ||
          item.ref ||
          item.id ||
          null;
        const {
          documentId,
          reference,
          oldReference,
          newReference,
          value,
          text,
          display,
          ref,
          id,
          ...rest
        } = item;
        const details = Object.keys(rest || {}).length ? JSON.stringify(rest) : null;
        return {
          key: `${documentId || referenceValue || 'unresolved'}-${index}`,
          documentId,
          reference: referenceValue || '-',
          details,
        };
      }),
    [unresolvedReferences],
  );
  const unresolvedHasDocumentId = useMemo(
    () => unresolvedReferenceRows.some((row) => row.documentId),
    [unresolvedReferenceRows],
  );
  const unresolvedHasDetails = useMemo(
    () => unresolvedReferenceRows.some((row) => row.details),
    [unresolvedReferenceRows],
  );

  const isWorking =
    candidatesPending || updateReferencesPending || sequencePending || sequenceInProgress;
  const isBlocked = !hasSelection || !isDocxFile;
  const isPrimaryActionDisabled =
    (activeStep === 0 && candidatesGenerated && hasUnresolvedReferences) ||
    (activeStep === 1 && !referencesUpdated);
  const caseId = useSelector(selectCurrentCaseId);
  const findFolderByName = useCallback((foldersToSearch = [], targetName) => {
    for (const folderItem of foldersToSearch) {
      if (!folderItem) continue;
      if (folderItem.name === targetName && !folderItem.readOnly) return folderItem;
      if (folderItem.folders && folderItem.folders.length) {
        const found = findFolderByName(folderItem.folders, targetName);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const findFolderById = useCallback((foldersToSearch = [], targetId) => {
    for (const folderItem of foldersToSearch) {
      if (!folderItem) continue;
      if (folderItem.id === targetId) return folderItem;
      if (folderItem.folders && folderItem.folders.length) {
        const found = findFolderById(folderItem.folders, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const ensureSequenceFolder = useCallback(
    async (selectedFolderId, bundleName) => {
      if (selectedFolderId && selectedFolderId !== 'auto-generated') {
        const existingFolder = findFolderById(
          [
            ...(folders.teamBundles || []),
            ...(folders.privateBundles || []),
            ...(folders.bundles || []),
            ...(folders.courtBundles || []),
            ...(folders.trialbooks || []),
          ],
          selectedFolderId,
        );
        return { id: selectedFolderId, name: existingFolder?.name || bundleName };
      }

      const resolvedName = (bundleName || '').trim();
      if (!resolvedName) {
        throw new Error(T.translate('case.updateReferencesSequenceBundleNameRequired'));
      }

      const existingFolder = findFolderByName([...(folders.teamBundles || [])], resolvedName);
      if (existingFolder && existingFolder.id) {
        return { id: existingFolder.id, name: existingFolder.name };
      }

      const addFolderResponse = await dispatch(
        addFolder({ name: resolvedName, type: 'team-bundle' }, { suppressFeedback: true }),
      );

      if (addFolderResponse && addFolderResponse.id) {
        try {
          await dispatch(fetchContainer({ folder: { type: 'team-bundle' } }));
        } catch {
          // Sidebar refresh is best-effort for now
        }
        return { id: addFolderResponse.id, name: resolvedName };
      }

      throw new Error(T.translate('case.updateReferencesSequenceBundleCreateFailed'));
    },
    [dispatch, folders, findFolderById, findFolderByName],
  );

  const sequenceDocuments = useMemo(() => {
    const candidates =
      referencesForSequence && referencesForSequence.length > 0
        ? referencesForSequence
        : selectedRows || [];
    const formatted = candidates
      .map((item) => {
        const id =
          item?.fileId || item?.documentId || item?.folderFileId || item?.compositeKey || item?.id;
        if (!id) return null;
        const name = item?.fileName || item?.name || item?.originalName || '';
        return { id, name };
      })
      .filter(Boolean);
    const unique = new Map();
    formatted.forEach(({ id, name }) => {
      if (!unique.has(id)) unique.set(id, name);
    });
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  }, [referencesForSequence, selectedRows]);

  const sequenceDocumentIds = useMemo(
    () => sequenceDocuments.map(({ id }) => id).filter(Boolean),
    [sequenceDocuments],
  );

  const handleGenerateSequence = handleSubmit(async ({ folder, bundleName }) => {
    setSequenceError('');
    if (!fileId) return;
    const resolvedBundleName = (bundleName || defaultSequenceBundleName || '').trim();

    setSequenceInProgress(true);
    try {
      const { id: targetFolderId, name: targetFolderName } = await ensureSequenceFolder(
        folder,
        resolvedBundleName,
      );

      if (sequenceDocuments.length > 0) {
        await dispatch(
          assignDocuments({
            documentIds: sequenceDocuments,
            folderId: targetFolderId,
            caseId,
          }),
        );
      }
      await dispatch(
        generateReferenceSequence({
          fileId,
          folderId: targetFolderId,
          bundleName: targetFolderName,
          presentMode: isPresentModePage,
          recycleBin: isRecycleBin,
          documentIds: sequenceDocumentIds,
          allReferences: referencesForSequence,
        }),
      );
    } catch (err) {
      console.log('error generating sequence', err);
      setSequenceError((err && err.message) || T.translate('case.updateReferencesSequenceError'));
    } finally {
      setSequenceInProgress(false);
    }
  });

  const handleUpdateReferences = useCallback(async () => {
    if (isBlocked || isWorking || noCandidates || !fileId) return;
    try {
      await dispatch(
        updateReferences(fileId, { presentMode: isPresentModePage, recycleBin: isRecycleBin }),
      );
    } catch {
      // Feedback toasts are handled by sagas for now.
    }
  }, [dispatch, fileId, isBlocked, isPresentModePage, isRecycleBin, isWorking, noCandidates]);

  const canAccessStep = useCallback(
    (stepIndex) => {
      if (stepIndex === 0) return true;
      if (stepIndex === 1) return candidatesGenerated && !hasUnresolvedReferences;
      if (stepIndex === 2)
        return candidatesGenerated && referencesUpdated && !hasUnresolvedReferences;
      return false;
    },
    [candidatesGenerated, hasUnresolvedReferences, referencesUpdated],
  );

  // useEffect(() => {
  //   if (referencesUpdated && activeStep === 1) {
  //     setActiveStep(2);
  //   }
  // }, [referencesUpdated, activeStep]);

  const handlePrimaryAction = async () => {
    if (isBlocked || isWorking || isPrimaryActionDisabled) return;
    try {
      if (activeStep === 0) {
        if (!candidatesGenerated) {
          await dispatch(
            generateReferenceCandidates(fileId, {
              presentMode: isPresentModePage,
              recycleBin: isRecycleBin,
            }),
          );
        } else {
          setActiveStep(1);
        }
        return;
      }
      if (activeStep === 1) {
        if (!referencesUpdated) return;
        setActiveStep(2);
        return;
      }
      if (activeStep === 2) {
        if (!sequenceGenerated) {
          await handleGenerateSequence();
        } else {
          handleClose();
        }
      }
    } catch {
      // Feedback toasts are handled by sagas for now.
    }
  };

  const handleGenerateCandidates = useCallback(async () => {
    if (isBlocked || isWorking || candidatesGenerated) return;
    try {
      await dispatch(
        generateReferenceCandidates(fileId, {
          presentMode: isPresentModePage,
          recycleBin: isRecycleBin,
        }),
      );
    } catch {
      // Feedback toasts are handled by sagas for now.
    }
  }, [
    isBlocked,
    isWorking,
    candidatesGenerated,
    dispatch,
    fileId,
    isPresentModePage,
    isRecycleBin,
  ]);

  const wizardSteps = useMemo(() => {
    const headerCellStyle = {
      textAlign: 'left',
      padding: '0.65rem 0.8rem',
      background: '#f8fafc',
      fontWeight: 700,
      fontSize: '0.9rem',
      borderBottom: '1px solid #e5e7eb',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    };
    const cellStyle = {
      padding: '0.65rem 0.8rem',
      borderBottom: '1px solid #eef2f7',
      fontSize: '0.9rem',
      wordBreak: 'break-word',
    };
    const sectionWrapperStyle = {
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      background: '#fff',
      overflow: 'hidden',
    };
    const sectionHeaderStyle = {
      padding: '0.65rem 0.85rem',
      background: '#f8fafc',
      borderBottom: '1px solid #e5e7eb',
    };
    const sectionDescriptionStyle = {
      padding: '0.6rem 0.85rem',
      borderBottom: '1px solid #e5e7eb',
      color: '#6b7280',
      fontSize: '0.85rem',
      lineHeight: 1.4,
    };

    const renderTable = (columns, rows, getRowKey) => (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={headerCellStyle}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const resolvedKey = getRowKey
              ? getRowKey(row, index)
              : row?.key ||
                row?.documentId ||
                row?.oldReference ||
                row?.newReference ||
                row?.reference ||
                index;
            return (
              <tr key={resolvedKey}>
                {columns.map((column) => (
                  <td key={column.key} style={cellStyle}>
                    {column.render ? column.render(row) : (row?.[column.key] ?? '-')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );

    const renderSection = ({
      title,
      description,
      columns,
      rows,
      getRowKey,
      wrapperStyle,
      headerStyle,
      descriptionStyle,
    }) => (
      <div style={{ ...sectionWrapperStyle, ...wrapperStyle }}>
        <div style={{ ...sectionHeaderStyle, ...headerStyle }}>
          <div style={{ fontWeight: 700 }}>
            {title} ({rows.length})
          </div>
        </div>
        {description ? (
          <div style={{ ...sectionDescriptionStyle, ...descriptionStyle }}>{description}</div>
        ) : null}
        {renderTable(columns, rows, getRowKey)}
      </div>
    );

    const updatedReferenceColumns = [
      {
        key: 'documentId',
        label: T.translate('case.documentId'),
        render: (row) => row?.documentId || '-',
      },
      {
        key: 'oldReference',
        label: T.translate('case.updateReferencesCandidatesHeaderOld'),
        render: (row) => row?.oldReference || '-',
      },
      {
        key: 'newReference',
        label: T.translate('case.updateReferencesCandidatesHeaderNew'),
        render: (row) => row?.newReference || '-',
      },
      {
        key: 'bundle',
        label: T.translate('case.bundle'),
        render: (row) => row?.bundle ?? '-',
      },
      {
        key: 'tab',
        label: T.translate('case.tab'),
        render: (row) => row?.tab ?? '-',
      },
      {
        key: 'paragraphIndex',
        label: T.translate('case.updateReferencesHeaderParagraph'),
        render: (row) => row?.paragraphIndex ?? '-',
      },
      {
        key: 'estimatedPage',
        label: T.translate('case.updateReferencesHeaderEstimatedPage'),
        render: (row) => row?.estimatedPage ?? '-',
      },
    ];

    const fileDocumentUpdateColumns = [
      {
        key: 'documentId',
        label: T.translate('case.documentId'),
        render: (row) => row?.documentId || '-',
      },
      {
        key: 'oldExhibitNumber',
        label: T.translate('case.updateReferencesHeaderOldExhibit'),
        render: (row) => row?.oldExhibitNumber || '-',
      },
      {
        key: 'newExhibitNumber',
        label: T.translate('case.updateReferencesHeaderNewExhibit'),
        render: (row) => row?.newExhibitNumber || '-',
      },
    ];

    const unresolvedReferenceColumns = [
      ...(unresolvedHasDocumentId
        ? [
            {
              key: 'documentId',
              label: T.translate('case.documentId'),
              render: (row) => row?.documentId || '-',
            },
          ]
        : []),
      {
        key: 'reference',
        label: T.translate('case.updateReferencesHeaderReference'),
        render: (row) => row?.reference || '-',
      },
      ...(unresolvedHasDetails
        ? [
            {
              key: 'details',
              label: T.translate('case.updateReferencesHeaderDetails'),
              render: (row) => row?.details || '-',
            },
          ]
        : []),
    ];

    const unresolvedSectionStyles = {
      wrapperStyle: {
        borderColor: '#fecaca',
        background: '#fff5f5',
      },
      headerStyle: {
        background: '#fee2e2',
      },
    };

    return [
      {
        key: 'candidates',
        // title: withFileId(T.translate('case.updateReferencesStepCandidatesTitle')),
        subtitle: T.translate('case.updateReferencesStepCandidatesSubtitle'),
        panelTitle: withFileId(T.translate('case.updateReferencesCandidatesPanelTitle')),
        panelDescription: '',
        content: (
          <div className="merge-grid merge-grid--single merge-grid--fill">
            <div style={{ color: '#4b5563', lineHeight: 1.5 }}>
              {T.translate('case.updateReferencesCandidatesPanelDescription')}
            </div>
            {!candidatesGenerated && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateCandidates}
                  disabled={candidatesPending}
                >
                  {T.translate('generic.generate')}
                </Button>
                {/* <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  {T.translate('case.updateReferencesCandidatesHint')}
                </div> */}
              </div>
            )}
            {candidatesGenerated && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  color: '#6b7280',
                  fontSize: '0.9rem',
                }}
              >
                <div>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    {T.translate('case.referencesFound')}
                  </span>{' '}
                  {referencesFoundCount}
                </div>
                <div>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    {T.translate('case.referencesUpdated')}
                  </span>
                  : {referencesUpdatedCount}
                </div>
              </div>
            )}
            <div
              style={{
                flex: 1,
                minHeight: '260px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {candidatesPending && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Spinner />
                </div>
              )}
              {!candidatesPending && candidatesGenerated && !hasPreviewData && (
                <div style={{ color: '#6b7280', padding: '1rem' }}>
                  {T.translate('case.updateReferencesCandidatesNone')}
                </div>
              )}
              {!candidatesPending && candidatesGenerated && hasPreviewData && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    padding: '0.85rem 0',
                  }}
                >
                  {unresolvedReferenceRows.length > 0 &&
                    renderSection({
                      title: withFileId(
                        T.translate('case.updateReferencesSectionUnresolvedReferencesTitle'),
                      ),
                      description: T.translate(
                        'case.updateReferencesSectionUnresolvedReferencesDescription',
                      ),
                      columns: unresolvedReferenceColumns,
                      rows: unresolvedReferenceRows,
                      getRowKey: (row, index) => row?.key || index,
                      ...unresolvedSectionStyles,
                    })}
                  {updatedReferences.length > 0 &&
                    renderSection({
                      title: withFileId(
                        T.translate('case.updateReferencesSectionUpdatedReferencesTitle'),
                      ),
                      description: T.translate(
                        'case.updateReferencesSectionUpdatedReferencesDescription',
                      ),
                      columns: updatedReferenceColumns,
                      rows: updatedReferences,
                      getRowKey: (row, index) =>
                        `${row?.documentId || row?.oldReference || row?.newReference || index}`,
                    })}
                  {fileDocumentUpdates.length > 0 &&
                    renderSection({
                      title: withFileId(
                        T.translate('case.updateReferencesSectionFileDocumentUpdatesTitle'),
                      ),
                      description: T.translate(
                        'case.updateReferencesSectionFileDocumentUpdatesDescription',
                      ),
                      columns: fileDocumentUpdateColumns,
                      rows: fileDocumentUpdates,
                      getRowKey: (row, index) =>
                        `${row?.documentId || row?.oldExhibitNumber || index}`,
                    })}
                  {collisionReassignments.length > 0 &&
                    renderSection({
                      title: withFileId(
                        T.translate('case.updateReferencesSectionCollisionReassignmentsTitle'),
                      ),
                      description: T.translate(
                        'case.updateReferencesSectionCollisionReassignmentsDescription',
                      ),
                      columns: fileDocumentUpdateColumns,
                      rows: collisionReassignments,
                      getRowKey: (row, index) =>
                        `${row?.documentId || row?.oldExhibitNumber || index}`,
                    })}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'update',
        // title: withFileId(T.translate('case.updateReferencesStepUpdateTitle')),
        subtitle: T.translate('case.updateReferencesStepUpdateSubtitle'),
        panelTitle: withFileId(T.translate('case.updateReferencesUpdatePanelTitle')),
        panelDescription: T.translate('case.updateReferencesUpdatePanelDescription'),
        content: (
          <div className="merge-grid merge-grid--single">
            <div style={{ fontWeight: 600 }}>
              {T.translate('case.updateReferencesCandidatesCount', {
                count: referencesFoundCount,
              })}
            </div>
            {noCandidates && !updateReferencesPending && !referencesUpdated && (
              <div style={{ color: '#6b7280' }}>
                {T.translate('case.updateReferencesCandidatesEmpty')}
              </div>
            )}
            {!referencesUpdated && !updateReferencesPending && !noCandidates && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                <div style={{ color: '#6b7280' }}>
                  {T.translate('case.updateReferencesUpdateReady')}
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateReferences}
                  disabled={isWorking || noCandidates}
                  size="small"
                  style={{ alignSelf: 'flex-start' }}
                >
                  {T.translate('generic.updateReferences')}
                </Button>
              </div>
            )}
            {updateReferencesPending && <Spinner />}
            {referencesUpdated && (
              <div style={{ color: '#2563eb', fontWeight: 600 }}>
                {T.translate('case.updateReferencesUpdateComplete')}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'sequence',
        // title: withFileId(T.translate('case.updateReferencesStepSequenceTitle')),
        subtitle: T.translate('case.updateReferencesStepSequenceSubtitle'),
        panelTitle: withFileId(T.translate('case.updateReferencesSequencePanelTitle')),
        panelDescription: T.translate('case.updateReferencesSequencePanelDescription'),
        content: (
          <div className="merge-grid merge-grid--single">
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#fff',
                padding: '0.85rem',
                marginBottom: '0.75rem',
              }}
            >
              <div style={{ fontWeight: 700 }}>
                {withFileId(T.translate('case.updateReferencesSequencePreviewTitle'))}
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.65rem' }}>
                {T.translate('case.updateReferencesSequencePreviewDescription')}
              </div>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  overflowX: 'auto',
                }}
              >
                {referencesForSequence.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.9rem' }}>
                    {T.translate('case.updateReferencesSequencePreviewNoData')}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '0.65rem 0.75rem',
                            background: '#f8fafc',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            borderBottom: '1px solid #e5e7eb',
                          }}
                        >
                          {T.translate('case.updateReferencesSequencePreviewHeaderExhibit')}
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '0.65rem 0.75rem',
                            background: '#f8fafc',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            borderBottom: '1px solid #e5e7eb',
                          }}
                        >
                          {T.translate('case.updateReferencesSequencePreviewHeaderFileId')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {referencesForSequence.map((reference, index) => (
                        <tr key={`${reference.fileId}-${reference.exhibitNumber || index}`}>
                          <td
                            style={{
                              padding: '0.65rem 0.75rem',
                              borderBottom: '1px solid #eef2f7',
                              fontSize: '0.85rem',
                            }}
                          >
                            {reference.exhibitNumber || '-'}
                          </td>
                          <td
                            style={{
                              padding: '0.65rem 0.75rem',
                              borderBottom: '1px solid #eef2f7',
                              fontSize: '0.85rem',
                            }}
                          >
                            {reference.fileId || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <TextForm
              {...register('bundleName', { required: true })}
              label={T.translate('case.updateReferencesSequenceBundleName')}
              placeholder={T.translate('case.updateReferencesSequenceBundleNamePlaceholder')}
              defaultValue={defaultSequenceBundleName}
              readOnly={sequencePending}
              autofocus
            />
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              {T.translate('case.updateReferencesSequenceBundleNameHelp')}
            </div>
            <MoveDocument
              title={T.translate('case.exhibitBundleToMoveTo')}
              control={control}
              errors={errors}
              canUnselect={false}
              readOnly={sequencePending}
              defaultFolderName={watchedBundleName || defaultSequenceBundleName}
              excludePublicContainers={true}
              menuPlacement="bottom"
            />
            {sequenceError && (
              <div style={{ color: '#ef4444', fontSize: '0.9rem' }}>{sequenceError}</div>
            )}
            {(sequencePending || sequenceInProgress) && <Spinner />}
            {sequenceGenerated && (
              <div style={{ color: '#2563eb', fontWeight: 600 }}>
                {T.translate('case.updateReferencesSequenceComplete')}
              </div>
            )}
          </div>
        ),
      },
    ];
  }, [
    candidatesGenerated,
    candidatesPending,
    collisionReassignments,
    defaultSequenceBundleName,
    errors,
    fileDocumentUpdates,
    handleGenerateCandidates,
    handleUpdateReferences,
    hasPreviewData,
    isWorking,
    noCandidates,
    referencesFoundCount,
    referencesUpdated,
    referencesUpdatedCount,
    register,
    sequenceError,
    sequenceGenerated,
    sequenceInProgress,
    sequencePending,
    unresolvedHasDetails,
    unresolvedHasDocumentId,
    unresolvedReferenceRows,
    updateReferencesPending,
    updatedReferences,
    control,
    watchedBundleName,
    withFileId,
  ]);

  const progressAmount = useMemo(() => {
    if (wizardSteps.length <= 1) return 0;
    return Math.min((activeStep / (wizardSteps.length - 1)) * 100, 100);
  }, [activeStep, wizardSteps.length]);

  const renderStepper = () => (
    <div className="merge-wizard__stepper">
      <div className="merge-wizard__progress">
        <div className="merge-wizard__progress-track" />
        <div className="merge-wizard__progress-active" style={{ width: `${progressAmount}%` }} />
      </div>
      {wizardSteps.map((step, index) => {
        const isActive = index === activeStep;
        const isComplete =
          (index === 0 && candidatesGenerated) ||
          (index === 1 && referencesUpdated) ||
          (index === 2 && sequenceGenerated);
        return (
          <button
            key={step.key}
            type="button"
            disabled={!canAccessStep(index) || isWorking || isBlocked}
            onClick={() => {
              if (!canAccessStep(index) || isWorking || isBlocked) return;
              setActiveStep(index);
            }}
            className={`merge-step ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''} ${
              !canAccessStep(index) || isWorking || isBlocked ? 'is-disabled' : ''
            }`}
          >
            <div className="merge-step__circle">
              {isComplete ? <Check fontSize="small" /> : index + 1}
            </div>
            <div className="merge-step__text">
              <div className="merge-step__title">{step.title}</div>
              <div className="merge-step__subtitle">{step.subtitle}</div>
            </div>
          </button>
        );
      })}
    </div>
  );

  const primaryActionLabel = useMemo(() => {
    if (activeStep === 0) {
      return candidatesGenerated ? T.translate('generic.next') : T.translate('generic.generate');
    }
    if (activeStep === 1) {
      return T.translate('generic.next');
    }
    if (activeStep === 2) {
      return sequenceGenerated
        ? T.translate('viewing.done')
        : T.translate('generic.generateSequence');
    }
    return T.translate('generic.next');
  }, [activeStep, candidatesGenerated, referencesUpdated, sequenceGenerated]);

  const showFooterAction = !(activeStep === 0 && !candidatesGenerated);
  const footerDisableSave = showFooterAction ? isWorking || isPrimaryActionDisabled : false;

  if (isBlocked) {
    return (
      <Modal
        show={show}
        title={withFileId(T.translate('generic.updateReferences'))}
        handleClose={handleClose}
        handleSave={null}
      >
        {hasSelection
          ? T.translate('case.updateReferencesDocxOnly')
          : T.translate('generic.noFilesSelected')}
      </Modal>
    );
  }

  return (
    <Modal
      className="merge-modal-dialog"
      show={show}
      title={withFileId(T.translate('generic.updateReferences'))}
      handleClose={handleClose}
      handleSave={showFooterAction ? handlePrimaryAction : null}
      saveTitle={showFooterAction ? primaryActionLabel : undefined}
      disableSave={footerDisableSave}
      buttons={
        <>
          {activeStep > 0 ? (
            <Button
              onClick={() => {
                if (activeStep > 0) {
                  setActiveStep((prev) => Math.max(prev - 1, 0));
                }
              }}
              variant="outlined"
              color="primary"
              disabled={isWorking}
            >
              {T.translate('generic.back')}
            </Button>
          ) : null}
        </>
      }
    >
      <PerfectScrollbar className="merge-scroll" options={{ suppressScrollX: true }}>
        <div className="merge-modal">
          <div className="merge-wizard">
            <div className="merge-intro">
              {T.translate('case.updateReferencesWizardIntro', { fileName: fileName || '-' })}
            </div>
            {renderStepper()}
            <div className="merge-panel">
              <div className="merge-panel__head">
                <div className="merge-panel__eyebrow">
                  Step {activeStep + 1} of {wizardSteps.length}
                </div>
                <div className="merge-panel__title">{wizardSteps[activeStep].panelTitle}</div>
                {wizardSteps[activeStep].panelDescription ? (
                  <div className="merge-panel__subtitle">
                    {wizardSteps[activeStep].panelDescription}
                  </div>
                ) : null}
              </div>
              <div className="merge-panel__body">{wizardSteps[activeStep].content}</div>
            </div>
          </div>
        </div>
      </PerfectScrollbar>
    </Modal>
  );
};

export default ModalUpdateReferences;
